import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { ColorPicker } from '../shared/ColorPicker';
import { Button } from '../shared/Button';
import { ALL_FONTS, isProFont } from '../../constants/fonts';
import { useHasFeature } from '../../hooks/useSubscription';

/**
 * StylePanel Component
 * Panel with color pickers for customizing text and background colors
 * Provides real-time preview updates through onChange callback
 */
export const StylePanel = ({
  style,
  onChange,
  onSave = null,
  imageUrl = null,
  brandColors = null,
  selectedFont = 'Classic',
  onFontChange = null,
  accountId = null
}) => {
  const { t } = useTranslation('approval');
  const [localStyle, setLocalStyle] = useState(style);

  // Check if user has access to pro fonts
  const { hasFeature: hasProFonts } = useHasFeature('pro_fonts', accountId);

  // Default style values
  const DEFAULT_STYLE = {
    textColor: '#FFFFFF',
    backgroundColor: '#000000',
    textHasBackground: true,
  };

  /**
   * Handle font change
   */
  const handleFontChange = (e) => {
    const fontId = e.target.value;

    // Check if trying to select a Pro font without access
    if (isProFont(fontId) && !hasProFonts) {
      // Don't allow selection
      return;
    }

    // Call onChange for live preview update
    if (onFontChange) {
      onFontChange(fontId);
    }
  };

  /**
   * Handle text color change
   */
  const handleTextColorChange = (color) => {
    const newStyle = { ...localStyle, textColor: color };
    setLocalStyle(newStyle);

    // Call onChange for live preview update
    if (onChange) {
      onChange(newStyle);
    }
  };

  /**
   * Handle background color change
   */
  const handleBackgroundColorChange = (color) => {
    const newStyle = { ...localStyle, backgroundColor: color };
    setLocalStyle(newStyle);

    // Call onChange for live preview update
    if (onChange) {
      onChange(newStyle);
    }
  };

  /**
   * Handle background toggle change
   */
  const handleBackgroundToggleChange = (e) => {
    const isChecked = e.target.checked;
    const newStyle = { ...localStyle, textHasBackground: isChecked };
    setLocalStyle(newStyle);

    // Call onChange for live preview update
    if (onChange) {
      onChange(newStyle);
    }
  };

  /**
   * Reset style to defaults
   */
  const handleResetStyle = () => {
    setLocalStyle(DEFAULT_STYLE);

    // Call onChange for live preview update
    if (onChange) {
      onChange(DEFAULT_STYLE);
    }

    // Optionally save the reset
    if (onSave) {
      onSave(DEFAULT_STYLE);
    }
  };

  /**
   * Check if current style differs from defaults
   */
  const hasChangesFromDefault = () => {
    return (
      localStyle.textColor !== DEFAULT_STYLE.textColor ||
      localStyle.backgroundColor !== DEFAULT_STYLE.backgroundColor ||
      localStyle.textHasBackground !== DEFAULT_STYLE.textHasBackground
    );
  };

  /**
   * Apply brand colors from brand settings
   */
  const handleApplyBrandColors = () => {
    if (!brandColors) return;

    const brandStyle = {
      textColor: brandColors.textColor,
      backgroundColor: brandColors.backgroundColor,
      textHasBackground: brandColors.textHasBackground
    };

    setLocalStyle(brandStyle);

    // Call onChange for live preview update
    if (onChange) {
      onChange(brandStyle);
    }

    // Optionally save the brand colors
    if (onSave) {
      onSave(brandStyle);
    }
  };

  return (
    <div className="style-panel space-y-6">
      {/* 1. Font Dropdown */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Schriftart
        </label>
        <select
          value={selectedFont}
          onChange={handleFontChange}
          className="
            w-full px-3 py-2
            border border-gray-300 rounded-lg
            focus:ring-2 focus:ring-blue-500 focus:border-blue-500
            bg-white text-gray-900
            cursor-pointer
          "
        >
          {ALL_FONTS.map((font) => {
            const isPro = isProFont(font.id);
            const isDisabled = isPro && !hasProFonts;

            return (
              <option
                key={font.id}
                value={font.id}
                disabled={isDisabled}
                className={isDisabled ? 'text-gray-400' : ''}
              >
                {font.name} {isPro && '🔒'} {isPro && !hasProFonts ? '(Only available in Pro)' : ''}
              </option>
            );
          })}
        </select>
        <p className="mt-1 text-xs text-gray-500">
          Choose the font style for your caption text
        </p>
      </div>

      {/* 2. Text Color Picker */}
      <div>
        <ColorPicker
          label={t('style.textColor.label')}
          value={localStyle.textColor}
          onChange={handleTextColorChange}
          className="style-panel-text-color"
          imageUrl={imageUrl}
        />
        <p className="mt-1 text-xs text-gray-500">
          {t('style.textColor.help')}
        </p>
      </div>

      {/* 3. Background Toggle */}
      <div className="flex items-start">
        <div className="flex items-center h-5">
          <input
            id="text-background-toggle"
            type="checkbox"
            checked={localStyle.textHasBackground}
            onChange={handleBackgroundToggleChange}
            className="
              h-4 w-4 text-blue-600 rounded
              border-gray-300
              focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
              cursor-pointer
            "
            aria-describedby="text-background-description"
          />
        </div>
        <div className="ml-3">
          <label
            htmlFor="text-background-toggle"
            className="text-sm font-medium text-gray-700 cursor-pointer"
          >
            {t('style.showBackground.label')}
          </label>
          <p
            id="text-background-description"
            className="text-xs text-gray-500 mt-1"
          >
            {t('style.showBackground.help')}
          </p>
        </div>
      </div>

      {/* 4. Background Color Picker (only if toggle is on) */}
      {localStyle.textHasBackground && (
        <div className="pl-7">
          <ColorPicker
            label={t('style.backgroundColor.label')}
            value={localStyle.backgroundColor}
            onChange={handleBackgroundColorChange}
            className="style-panel-bg-color"
            imageUrl={imageUrl}
          />
          <p className="mt-1 text-xs text-gray-500">
            {t('style.backgroundColor.help')}
          </p>
        </div>
      )}

      {/* Divider */}
      <div className="border-t border-gray-200 pt-6">
        {/* 5. Brand Colors Quick Apply Button */}
        {brandColors && (
          <div className="mb-4">
            <button
              onClick={handleApplyBrandColors}
              className="
                w-full px-4 py-2.5
                bg-gradient-to-r from-blue-50 to-indigo-50
                hover:from-blue-100 hover:to-indigo-100
                border-2 border-blue-200
                rounded-lg
                text-sm font-medium text-blue-700
                transition-all duration-200
                flex items-center justify-center gap-2
                shadow-sm hover:shadow-md
              "
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
              </svg>
              {t('style.brandColors.button')}
            </button>
            <p className="mt-1.5 text-xs text-gray-500 text-center">
              {t('style.brandColors.help')}
            </p>
          </div>
        )}

        {/* 6. Reset Button */}
        {hasChangesFromDefault() && (
          <Button
            variant="secondary"
            size="sm"
            onClick={handleResetStyle}
            className="w-full"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {t('style.button.reset')}
          </Button>
        )}

        {/* Optional: Save Button if onSave is provided */}
        {onSave && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => onSave(localStyle)}
            className="w-full mt-2"
          >
            {t('style.button.save')}
          </Button>
        )}
      </div>

      {/* Preview Hint */}
      <div className="mt-6 p-3 bg-blue-50 border border-blue-200 rounded-md">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-blue-600"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              {t('style.info')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

StylePanel.propTypes = {
  style: PropTypes.shape({
    textColor: PropTypes.string.isRequired,
    backgroundColor: PropTypes.string.isRequired,
    textHasBackground: PropTypes.bool.isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  imageUrl: PropTypes.string,
  brandColors: PropTypes.shape({
    textColor: PropTypes.string,
    backgroundColor: PropTypes.string,
    textHasBackground: PropTypes.bool,
  }),
  selectedFont: PropTypes.string,
  onFontChange: PropTypes.func,
  accountId: PropTypes.string,
};

export default StylePanel;
